import { LexoRank } from 'lexorank';

import { assertIsDefined } from '@gitbook/util-ts';

import { getEntropy } from './entropy';

export { disableOrderIndexEntropy, resetOrderIndexEntropy } from './entropy';

/**
 * Position at which to insert an item in an array, ordered by index.
 */
export interface OrderIndexPosition {
    /** orderIndex of the element before this position */
    before?: string;
    /** orderIndex of the element after this position */
    after?: string;
}

export interface Orderable {
    orderIndex: string;
}

/**
 * Compare two orderable items.
 *  returns -1 when @a comes first
 *  return +1 when @b comes first
 */
export function compareOrderIndexes(a: Orderable, b: Orderable): number {
    const aOrderIndex = a.orderIndex;
    const bOrderIndex = b.orderIndex;

    if (aOrderIndex < bOrderIndex) {
        return -1;
    }
    if (aOrderIndex > bOrderIndex) {
        return 1;
    }
    return 0;
}

/**
 * Find the position to insert a new element of an arbitrary order index into a sorted array
 * of orderable elements, using binary search.
 *
 * **Note:** The input array must be pre-sorted for this function to work correctly.
 */
export function findInsertPositionInSortedOrderIndexes<T extends Orderable>(
    array: T[],
    insertValue: T
) {
    let left = 0;
    let right = array.length;
    let leftLast = left;
    let rightLast = right;

    while (left < right) {
        const inPos = Math.floor((right + left) / 2);
        const current = array[inPos];
        assertIsDefined(current);

        const compared = compareOrderIndexes(current, insertValue);
        if (compared < 0) {
            left = inPos;
        } else if (compared > 0) {
            right = inPos;
        } else {
            right = inPos;
            left = inPos;
        }

        if (leftLast === left && rightLast === right) {
            break;
        }
        leftLast = left;
        rightLast = right;
    }

    return right;
}

/**
 * Sort elements that are orderable.
 * It mutates the array.
 */
export function sortOrderIndex<T extends Orderable>(elements: T[]): T[] {
    return elements.sort(compareOrderIndexes);
}

/**
 * Get position to insert element at the end of a list.
 */
export function getOrderIndexEndPosition<T extends Orderable>(elements: T[]): OrderIndexPosition {
    if (elements.length === 0) {
        return {};
    }

    const indexes = elements.map((element) => element.orderIndex).sort();
    return { before: indexes[indexes.length - 1] };
}

/**
 * Creates a new index positionned alphabetically between before and after.
 *
 * Ordering of elements in a collection without storing a global list of keys in another document.
 * It uses a lexicographical rank, similar to what Atlassian uses in Jira.
 *
 * To handle ordering in a partial view (e.g when a user move an element in a collection while not viewing all elements),
 * we add noises to the end results by offsetting the rank by a random number (between 0 and ENTROPY).
 * It can still result in a collision, but it's unlikely.
 *
 * References documents:
 *  - https://stackoverflow.com/a/49956113
 *  - https://confluence.atlassian.com/jirakb/understand-the-lexorank-managment-page-in-jira-server-779159218.html
 *  - https://begriffs.com/posts/2018-03-20-user-defined-order.html
 *
 * @param at.before Index of the element just before
 * @param at.after Index of the element just after
 */
export function createOrderIndex(at: OrderIndexPosition = {}): string {
    const before = at.before ? safeParse(at.before) : null;
    const after = at.after ? safeParse(at.after) : null;

    let result: LexoRank;

    if (before && !after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = before.genNext();
        for (let index = 0; index < noises; index += 1) {
            result = result.genNext();
        }
    } else if (!before && after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = after.genPrev();
        for (let index = 0; index < noises; index += 1) {
            result = result.genPrev();
        }
    } else if (before && after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = before.between(after);
        for (let index = 0; index < noises; index += 1) {
            result = result.between(after);
        }
    } else {
        result = getRandomLexoRank();
    }

    return result.toString();
}

/**
 * Safely parse a LexoRank.
 */
function safeParse(input: string): LexoRank {
    try {
        return LexoRank.parse(input);
    } catch (_err) {
        return getRandomLexoRank();
    }
}

/**
 * Get a default random LexoRank.
 */
export function getRandomLexoRank(): LexoRank {
    const noises = Math.floor(Math.random() * getEntropy());
    let result = LexoRank.middle();
    for (let index = 0; index < noises; index += 1) {
        result = result.genPrev();
    }
    return result;
}

export type { LexoRank };
